import React, { useMemo, useCallback } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { safeParseFloat } from 'libs/end-to-end/src/math';
import { CHART_COLORS } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/ChartColors';
import { TableViewModeArguments } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { useFormatCellValue } from 'apps/sigma2-ui/src/explore-section/hooks/useFormatCellValue';
import { truncateTextIfNecessary } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { SingleMetricTooltip } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/GenericTableViewTooltip';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { StackedBarChartPlaceholder } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/StackedBarChartPlaceholder';

/**
 * Stacked bar chart component for visualizing TableView data using Nivo.
 *
 * Features:
 * - Displays all metrics as separate horizontal bars (no metric selection needed)
 * - Each bar represents one metric with its own independent scale
 * - Each segment within a bar represents a category (groupBy dimension value)
 * - Color-coded segments using vibrant custom palette (matching pie charts)
 * - Single shared legend at top for all charts (categories are consistent across metrics)
 * - 2-column grid layout with 40px bar thickness
 * - Custom tooltip with formatted values
 * - Consistent theming with app design system
 *
 * @example
 * ```tsx
 * <StackedBarChartMode
 *   data={tableData}
 *   visibleRows={visibleRows}
 * />
 * ```
 */
export const StackedBarChartMode: React.FC<TableViewModeArguments> = ({
  data,
  visibleRows,
  viewConfiguration,
}) => {
  const { getColumnDisplayName } = useGetColumnDisplayName();
  const { formatCellValue } = useFormatCellValue();

  // Create one chart per metric (each with its own scale)
  const metricCharts = useMemo(() => {
    const categoryKeys: string[] = [];
    const categoryIndices: number[] = [];

    // Build list of categories (filter by visibleRows)
    data.rows.forEach((row, rowIndex) => {
      if (visibleRows.length === 0 || visibleRows.includes(rowIndex)) {
        categoryKeys.push(row[0]); // First column is the category (groupBy dimension)
        categoryIndices.push(rowIndex);
      }
    });

    // Create a separate chart for each metric
    const charts = data.columnHeaders.map((metricName, metricIndex) => {
      // Build data for this single metric
      const chartData = [
        {
          metric: metricName,
          ...categoryKeys.reduce(
            (acc, categoryName, categoryIdx) => {
              const rowIndex = categoryIndices[categoryIdx];
              const rows =
                viewConfiguration.overallDisplayMode === 'count'
                  ? data.numeratorSizes
                  : data.rows;
              const rawValue = rows[rowIndex][metricIndex + 1];
              const numericValue = safeParseFloat(rawValue.toString()) || 0;

              acc[categoryName] = numericValue;
              return acc;
            },
            {} as Record<string, number>,
          ),
        },
      ];

      return {
        metricName,
        data: chartData,
      };
    });

    return { charts, categoryKeys };
  }, [
    data.columnHeaders,
    data.numeratorSizes,
    data.rows,
    viewConfiguration.overallDisplayMode,
    visibleRows,
  ]);

  // Generate colors for each category - using the same vibrant custom palette as pie charts
  const categoryColors = useMemo(() => {
    return metricCharts.categoryKeys.map((_, index) => {
      return CHART_COLORS[index % CHART_COLORS.length];
    });
  }, [metricCharts.categoryKeys]);

  const getRowIndex = useCallback(
    (rowId: string) => {
      for (const rowMeta of data.rowsMeta) {
        if (rowMeta.groupByLevel === rowId) {
          return data.rowsMeta.indexOf(rowMeta);
        }
      }
      return 0;
    },
    [data.rowsMeta],
  );

  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  // Render a single metric's stacked bar chart (horizontal, one bar with stacked segments)
  const renderChart = useCallback(
    (chartData: Array<Record<string, string | number>>, metricName: string) => {
      // Single bar = 40px thick + margins + title
      const chartHeight = 120;

      const metricInfo = getMetricAndFocusAreaFromColumnName({
        columnName: metricName,
      });

      if (!metricInfo) {
        return null;
      }

      // if metric is not boolean and overall display mode is count, show placeholder instead
      if (
        !metricInfo.metric.isMetricBoolean &&
        viewConfiguration.overallDisplayMode === 'count'
      ) {
        return (
          <StackedBarChartPlaceholder
            key={metricName}
            metricName={metricName}
            chartHeight={chartHeight}
          />
        );
      }

      return (
        <div key={metricName} className="w-full">
          {/* Metric title */}
          <h3 className="text-base font-semibold text-gray-800 mb-3 px-2">
            {getColumnDisplayName(metricName)}
          </h3>

          <div
            className="w-full bg-white"
            style={{ height: `${chartHeight}px` }}
          >
            <ResponsiveBar
              data={chartData}
              keys={metricCharts.categoryKeys}
              indexBy="metric"
              layout="horizontal"
              margin={{ top: 10, right: 20, bottom: 50, left: 20 }}
              padding={0.25}
              valueScale={{ type: 'linear' }}
              indexScale={{ type: 'band', round: true }}
              colors={categoryColors}
              borderRadius={3}
              borderColor={{
                from: 'color',
                modifiers: [['darker', 0.2]],
              }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: 40,
                format: (value: string) =>
                  truncateTextIfNecessary({
                    text:
                      viewConfiguration.overallDisplayMode === 'count'
                        ? String(value)
                        : formatCellValue({
                            rawValue: value.toString(),
                            header: metricName,
                            units: viewConfiguration.units,
                          }),
                    maxChars: 6,
                  }),
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 0,
                tickRotation: 0,
                legend: '',
                legendPosition: 'middle',
                legendOffset: 0,
                format: () => '', // Hide tick labels since we only have one bar
              }}
              enableLabel={false}
              legends={[]} // No legend on individual charts
              tooltip={(datum) => {
                const metricIndex = data.columnHeaders.indexOf(metricName);
                const rowIndex = getRowIndex(String(datum.id));

                const rowValue =
                  safeParseFloat(
                    data.rows[rowIndex][metricIndex + 1].toString(),
                  ) || 0;
                const numeratorValue =
                  data.numeratorSizes?.[rowIndex]?.[metricIndex + 1] ?? 0;

                return (
                  <SingleMetricTooltip
                    metricColumn={metricName}
                    percentageValue={rowValue}
                    levelDisplayName={String(datum.id)}
                    totalSubjects={data.sampleSizes[rowIndex][metricIndex + 1]}
                    numeratorSize={numeratorValue}
                    units={viewConfiguration.units}
                    overallDisplayMode={viewConfiguration.overallDisplayMode}
                  />
                );
              }}
              theme={{
                text: {
                  fontFamily: KARLA_FONT_FAMILY,
                  fontSize: 13,
                },
                axis: {
                  domain: {
                    line: {
                      stroke: '#B8BABC',
                      strokeWidth: 1,
                    },
                  },
                  ticks: {
                    line: {
                      stroke: '#B8BABC',
                      strokeWidth: 1,
                    },
                  },
                },
                legends: {
                  text: {
                    fontSize: 12,
                  },
                },
                background: '#ffffff',
              }}
              motionConfig="gentle"
            />
          </div>
        </div>
      );
    },
    [
      getMetricAndFocusAreaFromColumnName,
      viewConfiguration.overallDisplayMode,
      viewConfiguration.units,
      getColumnDisplayName,
      metricCharts.categoryKeys,
      categoryColors,
      formatCellValue,
      data.columnHeaders,
      data.rows,
      data.numeratorSizes,
      data.sampleSizes,
      getRowIndex,
    ],
  );

  return (
    <div className="w-full bg-white py-4">
      {metricCharts.charts.length === 0 ? (
        <div className="w-full h-[400px] flex items-center justify-center text-gray-500">
          No data available to display
        </div>
      ) : (
        <>
          {/* Shared legend for all charts */}
          <div className="w-full mb-8 px-4">
            <div className="flex flex-wrap items-center gap-6">
              {metricCharts.categoryKeys.map((categoryKey, index) => (
                <div key={categoryKey} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: categoryColors[index] }}
                  />
                  <span className="text-xs text-gray-600">{categoryKey}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Chart grid */}
          <div className="w-full grid grid-cols-2 gap-6">
            {metricCharts.charts.map((chart) =>
              renderChart(chart.data, chart.metricName),
            )}
          </div>
        </>
      )}
    </div>
  );
};
